import React, { useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';

const EMOJIS = ['😡', '😕', '😐', '🙂', '😄'];
const EMOJI_VALUES = [1, 3, 5, 7, 10];

export default function MicroSurvey({ survey, token, onSuccess }) {
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (questionId, value) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }));
  };

  const isComplete = survey.questions.every((q) => {
    const val = responses[q.id];
    return val !== undefined && val !== null && val !== '';
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isComplete || submitting) return;

    setSubmitting(true);
    try {
      await api.post(`/surveys/${token}/responses`, {
        responses: Object.entries(responses).map(([questionId, value]) => ({
          question_id: parseInt(questionId, 10),
          value: typeof value === 'number' ? value : value,
        })),
      });
      toast.success('Merci pour vos réponses !');
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {survey.questions.map((q) => (
        <div key={q.id} className="space-y-4 transition-opacity duration-200">
          <label
            htmlFor={q.type === 'text_short' ? `q-${q.id}` : undefined}
            className="block text-base font-medium text-gray-700"
          >
            {q.label}
          </label>

          {q.type === 'score_emotion' && (
            <div className="flex flex-wrap gap-3">
              {EMOJIS.map((emoji, i) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => handleChange(q.id, EMOJI_VALUES[i])}
                  className={`min-w-[52px] min-h-[52px] text-3xl p-3 rounded-xl transition-all duration-200 active:scale-95 touch-manipulation ${
                    responses[q.id] === EMOJI_VALUES[i]
                      ? 'bg-primary-100 ring-2 ring-primary-500 shadow-sm'
                      : 'bg-gray-100 hover:bg-gray-200 active:bg-gray-200'
                  }`}
                  aria-label={`Score ${EMOJI_VALUES[i]} sur 10`}
                  aria-pressed={responses[q.id] === EMOJI_VALUES[i]}
                >
                  <span aria-hidden>{emoji}</span>
                </button>
              ))}
            </div>
          )}

          {q.type === 'ternary' && (
            <div className="flex flex-wrap gap-3">
              {[
                { label: '👍 Oui', value: 'yes' },
                { label: '👎 Non', value: 'no' },
                { label: '⚖️ Mitigé', value: 'mitigated' },
              ].map(({ label, value }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleChange(q.id, value)}
                  className={`min-h-[48px] px-5 py-3 rounded-xl font-medium transition-all duration-200 active:scale-[0.98] touch-manipulation ${
                    responses[q.id] === value
                      ? 'bg-primary-600 text-white shadow-md'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300 active:bg-gray-300'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {q.type === 'binary' && (
            <div className="flex gap-3">
              {[
                { label: 'Oui', value: 'yes' },
                { label: 'Non', value: 'no' },
              ].map(({ label, value }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleChange(q.id, value)}
                  className={`min-h-[48px] flex-1 px-5 py-3 rounded-xl font-medium transition-all duration-200 active:scale-[0.98] touch-manipulation ${
                    responses[q.id] === value
                      ? 'bg-primary-600 text-white shadow-md'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300 active:bg-gray-300'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {q.type === 'text_short' && (
            <input
              id={`q-${q.id}`}
              type="text"
              value={responses[q.id] || ''}
              onChange={(e) => handleChange(q.id, e.target.value)}
              maxLength={500}
              aria-label={q.label}
              className="w-full min-h-[48px] px-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-shadow"
              placeholder="Votre réponse..."
            />
          )}
        </div>
      ))}

      <button
        type="submit"
        disabled={!isComplete || submitting}
        className="w-full min-h-[52px] py-4 text-base font-medium rounded-xl bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 active:scale-[0.99] touch-manipulation shadow-md"
      >
        {submitting ? 'Envoi...' : 'Envoyer mes réponses'}
      </button>
    </form>
  );
}
